import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, type SupportedLanguage, type TranslationKeys, defaultLanguage } from '../locales';
import { getBasePathFromUrlPath, getLanguageFromUrlPath, isSupportedLanguage } from '../utils/localizedRoutes';

// Функция для определения языка системы
function getSystemLanguage(): SupportedLanguage {
  // Получаем язык браузера
  const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
  
  // Извлекаем код языка (первые 2 символа)
  const languageCode = browserLanguage.split('-')[0].toLowerCase() as SupportedLanguage;
  
  // Проверяем, поддерживается ли язык
  if (translations[languageCode]) {
    return languageCode;
  }
  
  // Возвращаем язык по умолчанию, если не поддерживается
  return defaultLanguage;
}

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  changeLanguage: (language: SupportedLanguage) => void;
  t: TranslationKeys;
  getTranslation: (key: string, params?: Record<string, string | number>) => any;
  availableLanguages: SupportedLanguage[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Определяем начальный язык: из URL path, legacy query, сохраненный в localStorage или системный
  const getInitialLanguage = (): SupportedLanguage => {
    const pathLanguage = getLanguageFromUrlPath(window.location.pathname);
    if (pathLanguage) {
      return pathLanguage;
    }

    // Legacy fallback: поддержка старых ссылок с ?lang=
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (isSupportedLanguage(urlLang)) {
      return urlLang;
    }

    // Для английских URL (/, /blog, /privacy, /terms и т.д.) язык должен быть en
    const basePath = getBasePathFromUrlPath(window.location.pathname);
    if (basePath) {
      return 'en';
    }
    
    // Затем проверяем localStorage
    const savedLanguage = localStorage.getItem('selectedLanguage') as SupportedLanguage;
    if (savedLanguage && translations[savedLanguage]) {
      return savedLanguage;
    }
    
    // Иначе используем системный язык
    return getSystemLanguage();
  };

  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getInitialLanguage());
  
  const t = translations[currentLanguage];
  
  // Слушаем изменения URL (back/forward и ручной ввод адреса)
  useEffect(() => {
    const syncLanguageFromUrl = () => {
      const pathLanguage = getLanguageFromUrlPath(window.location.pathname);
      const basePath = getBasePathFromUrlPath(window.location.pathname);
      const urlParams = new URLSearchParams(window.location.search);
      const legacyQueryLang = urlParams.get('lang');
      const nextLanguage =
        pathLanguage ||
        (isSupportedLanguage(legacyQueryLang) ? legacyQueryLang : null) ||
        (basePath ? 'en' : null);

      if (nextLanguage && nextLanguage !== currentLanguage) {
        setCurrentLanguage(nextLanguage);
        localStorage.setItem('selectedLanguage', nextLanguage);
      }
    };

    const handlePopState = () => {
      syncLanguageFromUrl();
    };

    syncLanguageFromUrl();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentLanguage]);
  
  const changeLanguage = (language: SupportedLanguage) => {
    if (import.meta.env.DEV) {
      console.log('Changing language from', currentLanguage, 'to', language);
    }
    setCurrentLanguage(language);
    localStorage.setItem('selectedLanguage', language);
  };

  // Helper function to get nested translation with interpolation
  const getTranslation = (key: string, params?: Record<string, string | number>) => {
    const keys = key.split('.');
    let value: any = t;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value === 'string' && params) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() || match;
      });
    }
    
    return value || key;
  };


  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    t,
    getTranslation,
    availableLanguages: Object.keys(translations) as SupportedLanguage[]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslations() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a LanguageProvider');
  }
  return context;
}
